import {
  CycleStage,
  SubmissionChannel,
  SubmissionSource,
  SubmissionStatus,
  type CycleStandingResponse,
  type GroupMemberResponse,
  type SubmissionResponse,
} from '../../api';

/**
 * The words the submission screen uses, and the one rule it enforces on its own.
 *
 * A lookup module like [`reviewQueue`](./reviewQueue.ts), and here for the same
 * reason: a stage appears on the calendar line, in the panel heading and in the
 * history table, and "Enviado" spelled two ways across the three is how a screen
 * stops being trusted.
 *
 * The failure reasons are the exception and are the server's own words, the way
 * the review queue's flag messages are. "O portal recusou o arquivo: CPF
 * inválido na linha 41" is a sentence a table here could not produce, and it is
 * the only thing whoever prepares the next attempt has to go on.
 */

/** How far through the month a contract has got. */
export const STAGE_LABELS: Record<CycleStage, string> = {
  [CycleStage.OPEN]: 'Em análise',
  [CycleStage.APPROVED]: 'Aprovado, a enviar',
  [CycleStage.PREPARED]: 'Arquivo gerado',
  [CycleStage.SENT]: 'Enviado',
  [CycleStage.FAILED]: 'Recusado pela operadora',
};

/** Where one attempt stands. */
export const SUBMISSION_STATUS_LABELS: Record<SubmissionStatus, string> = {
  [SubmissionStatus.PREPARED]: 'Gerado',
  [SubmissionStatus.SENT]: 'Enviado',
  [SubmissionStatus.FAILED]: 'Recusado',
};

/** Which door the file went through. */
export const CHANNEL_LABELS: Record<SubmissionChannel, string> = {
  [SubmissionChannel.EMAIL]: 'E-mail',
  [SubmissionChannel.INSURER_PORTAL]: 'Portal da operadora',
  [SubmissionChannel.SFTP]: 'SFTP',
  [SubmissionChannel.OTHER]: 'Outro',
};

/**
 * What produced the record.
 *
 * Worth showing rather than hiding: a `manual` row is somebody asserting that a
 * month went out, with no file behind it and none claimed. Reading it beside a
 * `cycle` row as though the two evidenced the same thing is exactly the mistake
 * the column exists to prevent.
 */
export const SOURCE_LABELS: Record<SubmissionSource, string> = {
  [SubmissionSource.CYCLE]: 'Ciclo',
  [SubmissionSource.MANUAL]: 'Marcação manual',
};

/** The channels offered, in the order an analyst is likely to want them. */
export const CHANNEL_OPTIONS: readonly {
  readonly value: SubmissionChannel;
  readonly label: string;
}[] = [
  SubmissionChannel.INSURER_PORTAL,
  SubmissionChannel.EMAIL,
  SubmissionChannel.SFTP,
  SubmissionChannel.OTHER,
].map((value) => ({ value, label: CHANNEL_LABELS[value] }));

export const SUBMISSION_COPY = {
  title: 'Envios do mês',
  description:
    'O fechamento de cada contrato até o fim: aprovado, arquivo gerado, enviado à operadora e ' +
    'marcado como enviado com o comprovante que voltou.',
  cycleLabel: 'Ciclo',
  cycleHint: 'O mês cujo envio você está acompanhando.',
  calendarTitle: 'Contratos',
  calendarDescription:
    'Um contrato por linha, com até onde o fechamento chegou neste mês e se o corte já passou ' +
    'sem envio. Quando a operadora recebe um arquivo único por empresa, os contratos dela ' +
    'aparecem juntos numa linha só — e, quando a empresa faz parte de um grupo, os contratos ' +
    'de todas as empresas do grupo aparecem juntos numa linha do grupo.',
  noContracts: 'Nenhum contrato no seu escopo.',
  outstanding: 'A enviar',
  overdue: 'Em atraso',
  open: 'Abrir',
  standingTitle: 'Fechamento deste contrato',
  contractTitle: 'Envio deste contrato',
  backToCalendar: 'Voltar para os envios do mês',
  historyTitle: 'Histórico de envios',
  historyDescription:
    'Todas as tentativas deste contrato, inclusive as recusadas, do ciclo mais recente para o ' +
    'mais antigo. Uma tentativa recusada continua aqui: é ela que responde quantas vezes ' +
    'tentamos enviar o mês.',
  noHistory: 'Nenhum envio registrado para este contrato.',

  // --- The three actions ---------------------------------------------------
  prepare: 'Gerar arquivo para envio',
  markSent: 'Marcar como enviado',
  markFailed: 'Registrar recusa',
  confirmSent: 'Confirmar envio',
  confirmFailed: 'Confirmar recusa',
  cancel: 'Cancelar',
  prepared: 'Arquivo gerado. Baixe-o, envie à operadora e volte para marcar como enviado.',
  sent: 'Envio registrado.',
  failed: 'Recusa registrada. O ciclo continua aprovado: gere o arquivo de novo quando resolver.',
  downloadFile: 'Baixar arquivo',
  downloading: 'Baixando…',
  downloadFailed: 'Não foi possível baixar o arquivo. Tente de novo em instantes.',

  // --- What each state tells the analyst to do next ------------------------
  needsApproval:
    'Este ciclo ainda não foi aprovado. A aprovação na fila de análise é o que garante que o ' +
    'arquivo que sai é o que alguém leu — sem ela não há o que enviar.',
  readyToPrepare:
    'Ciclo aprovado e arquivo congelado. Gere o arquivo para envio: isso registra que ele saiu ' +
    'daqui, e é o passo que separa "aprovado" de "enviado".',
  preparedFootnote:
    'O arquivo saiu daqui e ainda não chegou à operadora. Envie-o e volte para marcar como ' +
    'enviado — ou registre a recusa, se a operadora não o aceitou.',
  sentFootnote:
    'Mês fechado do nosso lado. A partir daqui, uma movimentação com vigência dentro deste ' +
    'período é uma chegada fora do prazo, e a aprovação do ciclo não pode mais ser reaberta.',

  // --- The marker ----------------------------------------------------------
  channelLabel: 'Por onde foi enviado',
  channelHint: 'Metade de qualquer prova de envio; a outra metade é o comprovante abaixo.',
  sentAtLabel: 'Quando foi enviado',
  sentAtHint: 'Deixe em branco para registrar agora. Uma data no futuro é recusada.',
  referenceLabel: 'Protocolo',
  referenceHint: 'O número ou identificador que a operadora devolveu, se devolveu algum.',
  proofDocumentLabel: 'Comprovante (id do documento)',
  proofDocumentHint:
    'Um comprovante já enviado ao acervo desta empresa. Faça o upload em Documentos primeiro.',
  notesLabel: 'Observações',
  notesHint:
    'O que a próxima pessoa precisa saber. É aqui que cabe o caso honesto: "enviado às 14:02, ' +
    'o portal não devolveu protocolo".',
  reasonLabel: 'Motivo da recusa',
  reasonHint:
    'O que a operadora disse, ou o que deu errado no caminho. Quem for preparar a próxima ' +
    'tentativa só tem isto.',
  proofRequired:
    'Diga o que voltou: protocolo, comprovante ou uma observação — pelo menos um dos três. Um ' +
    'mês marcado como enviado sem nada por trás não comprova nada.',
  // --- One file across a company's contracts ------------------------------
  consolidatedTitle: 'Envio único da empresa',
  consolidatedDescription:
    'Esta operadora recebe um só arquivo por empresa e mês, com todos os contratos dela. Cada ' +
    'contrato continua aprovado por conta própria; o arquivo reúne as linhas aprovadas de ' +
    'todos, em ordem de subestipulante.',
  consolidatedStandingTitle: 'Fechamento do arquivo único',
  membersTitle: 'Contratos neste arquivo',
  membersDescription:
    'Todos os contratos da empresa com esta operadora, na ordem em que entram no arquivo. ' +
    'Marcar o arquivo como enviado ou recusado marca cada um deles da mesma forma.',
  attemptsTitle: 'Arquivos deste mês',
  attemptsDescription: 'Todos os arquivos únicos gerados para este mês, inclusive os recusados.',
  noAttempts: 'Nenhum arquivo gerado para este mês.',
  prepareConsolidated: 'Gerar arquivo único',
  preparedConsolidated:
    'Arquivo único gerado. Baixe-o, envie à operadora e volte para marcar como enviado.',
  readyToPrepareConsolidated:
    'Todos os contratos estão aprovados. Gere o arquivo único: ele reúne as linhas aprovadas ' +
    'de cada contrato e registra que o mês saiu daqui.',
  missingApprovals: 'O arquivo único só pode ser gerado com todos os contratos aprovados. Faltam:',
  filedTogether:
    'Este contrato é enviado no arquivo único da empresa com esta operadora. Gere o arquivo, ' +
    'marque como enviado ou registre a recusa por lá.',
  openConsolidated: 'Abrir envio único',

  // --- One file across an economic group's companies ----------------------
  groupLabel: 'Grupo',
  groupTitle: 'Envio único do grupo',
  groupDescription:
    'Esta operadora recebe um só arquivo por cliente e mês, e as empresas deste grupo são um ' +
    'cliente só. O arquivo reúne as linhas aprovadas de todos os contratos das empresas do ' +
    'grupo, em ordem de subestipulante e módulo; cada contrato continua aprovado por conta ' +
    'própria.',
  groupStandingTitle: 'Fechamento do arquivo do grupo',
  groupMembersTitle: 'Empresas e contratos neste arquivo',
  groupMembersDescription:
    'Os contratos das empresas do grupo com esta operadora, por empresa. Depois que o arquivo ' +
    'é gerado, ele mantém os contratos que reuniu, mesmo que o grupo mude. Marcar o arquivo ' +
    'como enviado ou recusado marca cada um deles da mesma forma.',
  groupAttemptsDescription:
    'Todos os arquivos do grupo gerados para este mês, inclusive os recusados.',
  prepareGroup: 'Gerar arquivo do grupo',
  preparedGroup:
    'Arquivo do grupo gerado. Baixe-o, envie à operadora e volte para marcar como enviado.',
  readyToPrepareGroup:
    'Todos os contratos do grupo estão aprovados. Gere o arquivo do grupo: ele reúne as linhas ' +
    'aprovadas de cada contrato de cada empresa e registra que o mês saiu daqui.',
  groupMissingApprovals:
    'O arquivo do grupo só pode ser gerado com todos os contratos aprovados. Faltam:',
  groupAmbiguous:
    'Dois contratos do grupo têm o mesmo subestipulante e módulo nesta operadora, e num arquivo ' +
    'só a operadora não saberia distinguir as linhas. Corrija os contratos antes de gerar:',
  groupHeldElsewhere:
    'Estes contratos estão num arquivo já gerado e ainda não resolvido — em geral o arquivo ' +
    'único da empresa, gerado antes de ela entrar no grupo. Marque esse arquivo como enviado ' +
    'ou recusado onde ele está; depois gere o arquivo do grupo:',
  groupAlreadyFiled:
    'Estes contratos já foram enviados neste mês em outro arquivo e ficam fora do arquivo do ' +
    'grupo, para não serem enviados duas vezes:',
  filedBeforeGroup:
    'Este arquivo foi gerado antes de a empresa entrar no grupo. Registre aqui o que aconteceu ' +
    'com ele — enviado ou recusado; o arquivo do grupo espera por isso.',
  groupProofRequired:
    'Diga o que voltou: protocolo ou uma observação — pelo menos um dos dois. Um mês marcado ' +
    'como enviado sem nada por trás não comprova nada.',
  filedWithGroup:
    'Esta empresa faz parte de um grupo, e esta operadora recebe um arquivo só para o grupo ' +
    'inteiro. Gere o arquivo, marque como enviado ou registre a recusa no envio do grupo.',
  openGroup: 'Abrir envio do grupo',
  noTransmission:
    'Nada é transmitido por aqui. Estas telas registram o que uma pessoa fez com o arquivo; ' +
    'entregá-lo ao sistema da operadora é um trabalho à parte, que depende de sabermos o que ' +
    'aquela operadora aceita.',
} as const;

/**
 * Whether this attempt can still be marked sent or refused.
 *
 * A named predicate because the screen asks it in three places and the answer is
 * not "it is not sent": a refused attempt is closed too, and offering the marker
 * on one would be offering to overwrite a recorded outcome.
 */
export function isOpenAttempt(submission: SubmissionResponse | null | undefined): boolean {
  return submission != null && submission.status === SubmissionStatus.PREPARED;
}

/**
 * What was recorded as proof, in the order it is worth reading.
 *
 * Returns the pieces rather than a sentence so the panel can show "nothing" as
 * loudly as it shows a protocol number — on a `sent` attempt this list is never
 * empty, and one that was would be the record failing at the only thing it is
 * for.
 */
export function proofOf(submission: SubmissionResponse): readonly string[] {
  const parts: string[] = [];
  if (submission.reference != null && submission.reference !== '') {
    parts.push(`Protocolo ${submission.reference}`);
  }
  if (submission.proof_document_id != null) {
    parts.push('Comprovante anexado');
  }
  if (submission.notes != null && submission.notes !== '') {
    parts.push(submission.notes);
  }
  return parts;
}

/** The current month as the API writes a cycle: `YYYY-MM`. */
export function currentCycleMonth(today: Date = new Date()): string {
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * The standing of one contract after a write, computed from what the write
 * itself returned.
 *
 * The panel on the detail page and the line on the calendar both render from
 * `CycleStandingResponse` -- `submission`, `stage`, `last_failure`, `is_overdue`.
 * A mutation returns the new `SubmissionResponse`, and the standing the server
 * would next report is a deterministic function of that submission plus the
 * standing as it stood. So the cache can be patched in place from the
 * mutation's own response, without waiting for the refetch that
 * `invalidateQueries` would trigger.
 *
 * Doing it this way is what makes the panel and the calendar move on the same
 * render as the click, with no round trip in between -- the bug this fixed was
 * a screen that stayed on the old stage until somebody pressed F5. The write's
 * own response is the freshest statement of the new standing there is, so
 * nothing is gained by asking the server to repeat it, and a listing that has
 * not caught up cannot undo it.
 */
export function applySubmissionToStanding(
  standing: CycleStandingResponse,
  submission: SubmissionResponse,
): CycleStandingResponse {
  if (submission.status === SubmissionStatus.FAILED) {
    // A refusal closes the attempt and parks the cycle back at the gate, with
    // the refusal carried as `last_failure` rather than the live submission.
    // The cycle month and cutoff do not move.
    return {
      ...standing,
      submission: null,
      last_failure: submission,
      stage: CycleStage.FAILED,
      is_overdue: standing.is_past_cutoff,
    };
  }
  return {
    ...standing,
    submission,
    stage: submission.status === SubmissionStatus.SENT ? CycleStage.SENT : CycleStage.PREPARED,
    // Only `sent` closes the cycle for the overdue check.
    is_overdue: standing.is_past_cutoff && submission.status !== SubmissionStatus.SENT,
  };
}

/** How a contract is named to a person: by the insurer's two codes. */
export function contractLabel(line: { subestipulante: number; modulo: number }): string {
  return `subestipulante ${line.subestipulante} / módulo ${line.modulo}`;
}

/** "2 de 3 aprovados", for a company's single file. */
export function approvedOf(approved: number, total: number): string {
  return `${approved} de ${total} ${total === 1 ? 'aprovado' : 'aprovados'}`;
}

/**
 * One line of the calendar: a contract filed on its own, every contract a
 * company has with an insurer that takes them in a single file, or -- when the
 * company is in an economic group -- every such contract of the whole group.
 */
export type CalendarLine =
  | { readonly kind: 'contract'; readonly key: string; readonly standing: CycleStandingResponse }
  | {
      readonly kind: 'consolidated' | 'group';
      readonly key: string;
      readonly members: readonly CycleStandingResponse[];
      readonly stage: CycleStage;
      readonly approvedCount: number;
    };

/**
 * The calendar's contracts, with those that share a single file folded into one
 * line per company (or per group) and insurer -- at the position of the first
 * of them, so the listing's order is otherwise the server's.
 */
export function calendarLines(items: readonly CycleStandingResponse[]): CalendarLine[] {
  const lines: CalendarLine[] = [];
  const groups = new Map<string, CycleStandingResponse[]>();
  for (const item of items) {
    if (!item.consolidated) {
      lines.push({ kind: 'contract', key: item.contract_id, standing: item });
      continue;
    }
    const grouped = item.group_id != null;
    const key = grouped
      ? `group:${item.group_id}:${item.insurer_id}`
      : `${item.company_id}:${item.insurer_id}`;
    const members = groups.get(key);
    if (members !== undefined) {
      members.push(item);
      continue;
    }
    groups.set(key, [item]);
    lines.push({
      kind: grouped ? 'group' : 'consolidated',
      key,
      members: [],
      stage: CycleStage.OPEN,
      approvedCount: 0,
    });
  }
  return lines.map((line) => {
    if (line.kind === 'contract') {
      return line;
    }
    const members = groups.get(line.key) ?? [];
    return {
      ...line,
      members,
      stage: consolidatedStage(members, line.kind),
      approvedCount: members.filter((member) => member.approval != null).length,
    };
  });
}

/**
 * The month of a company's (or a group's) single file, read off its members.
 *
 * The file stands when every member's own attempt names the same one; until
 * then the month is held by whichever member is furthest behind.
 */
export function consolidatedStage(
  members: readonly CycleStandingResponse[],
  kind: 'consolidated' | 'group' = 'consolidated',
): CycleStage {
  const files = new Set(
    members.map((member) =>
      kind === 'group'
        ? member.submission?.group_submission_id
        : member.submission?.consolidated_submission_id,
    ),
  );
  const [only] = [...files];
  if (files.size === 1 && only != null) {
    return members[0]?.submission?.status === SubmissionStatus.SENT
      ? CycleStage.SENT
      : CycleStage.PREPARED;
  }
  if (members.some((member) => member.stage === CycleStage.OPEN)) {
    return CycleStage.OPEN;
  }
  if (members.some((member) => member.stage === CycleStage.FAILED)) {
    return CycleStage.FAILED;
  }
  return CycleStage.APPROVED;
}

/**
 * The members by company, each company once, in the order its first contract
 * comes in the file -- which is also the order of the rows within each.
 */
export function byCompany(
  members: readonly GroupMemberResponse[],
): [string, GroupMemberResponse[]][] {
  const companies = new Map<string, [string, GroupMemberResponse[]]>();
  for (const member of members) {
    const entry = companies.get(member.company_id);
    if (entry !== undefined) {
      entry[1].push(member);
    } else {
      companies.set(member.company_id, [member.company_name, [member]]);
    }
  }
  return [...companies.values()];
}
